/**
 * LiveTestingQuestionFieldInner
 *
 * 모달 내부에서 질문을 렌더링하기 위한 내부 컴포넌트
 * LiveTestingContainer에 의존하지 않음
 *
 * V2QsSelect, V2QsText, V2QsNumber 등 기본 입력 컴포넌트만 지원
 */

import { memo, useCallback, useMemo } from 'react';
import type { QuestionResponseDto } from '../../../../api/generated/types';
import type { LiveTestingAnswerValue } from './types';
import { isSelectType } from './types';
import { useTranslations } from '../../../../../patient/hooks/use-translations';
import { useStaticLocales, type SystemLocaleCodes, type SystemLocaleKeys } from '../../../../../admin/hooks/locales/use-static-locale';
import { getLocaleCode } from '../../../organization/configurations/language/constants';

// V2Qs 컴포넌트 import
import { V2QsBloodPressure, V2QsBloodPressureValue } from '../../../../shared/components/question-types/V2QsBloodPressure';
import { V2QsDate } from '../../../../shared/components/question-types/V2QsDate';
import { V2QsDateDob } from '../../../../shared/components/question-types/V2QsDateDob';
import { V2QsDatetime } from '../../../../shared/components/question-types/V2QsDatetime';
import { V2QsFrequency, V2QsFrequencyValue } from '../../../../shared/components/question-types/V2QsFrequency';
import { V2QsNumber } from '../../../../shared/components/question-types/V2QsNumber';
import { V2QsRange } from '../../../../shared/components/question-types/V2QsRange';
import { V2QsEqVas } from '../../../../shared/components/question-types/V2QsEqVas';
import { V2QsRelativeOnset, V2QsRelativeOnsetValue, V2QsRelativeOnsetUnit } from '../../../../shared/components/question-types/V2QsRelativeOnset';
import { V2QsSelect, V2QsSelectOption, V2QsSelectValue } from '../../../../shared/components/question-types/V2QsSelect';
import { V2QsSelectSheet, V2QsSelectSheetValue } from '../../../../shared/components/question-types/V2QsSelectSheet';
import LiveTestingBodySelectField, { BodySelectValue } from './LiveTestingBodySelectField';
import LiveTestingHeadSelectField from './LiveTestingHeadSelectField';
import LiveTestingBodySelectTreeField, { BodySelectTreeValue } from './LiveTestingBodySelectTreeField';
import { V2QsText } from '../../../../shared/components/question-types/V2QsText';
import { V2QsTextarea } from '../../../../shared/components/question-types/V2QsTextarea';
import { V2QsTime } from '../../../../shared/components/question-types/V2QsTime';
import LiveTestingSxSelectField from './LiveTestingSxSelectField';
import {
  genEqVasTopLabelKey,
  genEqVasBottomLabelKey,
  genEqVasValueBoxLabelKey,
  genScaleLabelKey,
} from '../questions/useQuestionConfigureModal';
import { normalizeQuestionLabelForSurveyLocale } from '../../../../shared/utils/survey-locale-text';

interface LiveTestingQuestionFieldInnerProps {
  /** 질문 데이터 */
  question: QuestionResponseDto;
  /** 현재 답변 값 */
  value: LiveTestingAnswerValue;
  /** 값 변경 핸들러 */
  onChange: (value: LiveTestingAnswerValue) => void;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** 성별 (body selector 이미지용) */
  isMale?: boolean;
  /** 번역 locale */
  locale?: string;
}

/**
 * 모달 내부에서 질문 필드를 렌더링
 * 기본 입력은 독립적으로 동작하며, 일부 타입은 LiveTestingContainer에 의존
 */
function LiveTestingQuestionFieldInner({
  question,
  value,
  onChange,
  disabled = false,
  isMale = true,
  locale = 'english',
}: LiveTestingQuestionFieldInnerProps) {
  const { inputType, answerKeys, answerOptionsMap, typeOptions } = question;
  const [, { t }] = useTranslations({ locale });
  const forcedLocaleCode = useMemo<SystemLocaleCodes>(
    () => getLocaleCode(locale, 'en') as SystemLocaleCodes,
    [locale]
  );
  const { sltStr } = useStaticLocales({ localeCode: forcedLocaleCode });
  const trWithStaticFallback = useCallback(
    (key: string, fallbackValue: string, staticKey: SystemLocaleKeys) => {
      const staticTranslated = sltStr({ key: staticKey });
      if (staticTranslated && staticTranslated !== staticKey) {
        return staticTranslated;
      }
      const translated = t(key, null, { fallbackValue: null });
      if (translated && translated !== key) {
        return translated;
      }
      return fallbackValue;
    },
    [sltStr, t]
  );

  // Select 타입용 옵션 변환
  const selectOptions = useMemo<V2QsSelectOption[]>(() => {
    if (!isSelectType(inputType) || !answerKeys) return [];

    return answerKeys.map((key) => {
      const optionData = answerOptionsMap?.[key];
      return {
        key,
        label: (optionData?.label as string) || key,
        requireNote: Boolean(optionData?.requireNote),
        mutualExclusive: Boolean(optionData?.mutuallyExclusive),
        disabled: false,
      };
    });
  }, [inputType, answerKeys, answerOptionsMap]);

  // Select 값 핸들러
  const handleSelectChange = useCallback(
    (newValue: V2QsSelectValue) => {
      onChange(newValue as LiveTestingAnswerValue);
    },
    [onChange]
  );

  // Text 값 핸들러
  const handleTextChange = useCallback(
    (newValue: string) => {
      onChange(newValue);
    },
    [onChange]
  );

  // Number 값 핸들러
  const handleNumberChange = useCallback(
    (newValue: number | undefined) => {
      onChange(newValue);
    },
    [onChange]
  );

  // BloodPressure 값 핸들러
  const handleBloodPressureChange = useCallback(
    (newValue: V2QsBloodPressureValue) => {
      onChange(newValue as LiveTestingAnswerValue);
    },
    [onChange]
  );

  // Frequency 값 핸들러
  const handleFrequencyChange = useCallback(
    (newValue: V2QsFrequencyValue) => {
      onChange(newValue as LiveTestingAnswerValue);
    },
    [onChange]
  );

  // RelativeOnset 값 핸들러
  const handleRelativeOnsetChange = useCallback(
    (newValue: V2QsRelativeOnsetValue) => {
      onChange(newValue as LiveTestingAnswerValue);
    },
    [onChange]
  );
  const localizedUnitOptions = useMemo(
    () => {
      const stripDurationToken = (label: string) => label.replace(/\{\{v0\}\}\s*/g, '');
      return [
        {
          value: 'minute',
          labelSingular: stripDurationToken(trWithStaticFallback('minute ago', 'minute ago', 'minute ago')),
          labelPlural: stripDurationToken(trWithStaticFallback('minutes ago', 'minutes ago', 'minutes ago')),
        },
        {
          value: 'hour',
          labelSingular: stripDurationToken(trWithStaticFallback('hour ago', 'hour ago', 'hour ago')),
          labelPlural: stripDurationToken(trWithStaticFallback('hours ago', 'hours ago', 'hours ago')),
        },
        {
          value: 'day',
          labelSingular: stripDurationToken(trWithStaticFallback('day ago', 'day ago', 'day ago')),
          labelPlural: stripDurationToken(trWithStaticFallback('days ago', 'days ago', 'days ago')),
        },
        {
          value: 'week',
          labelSingular: stripDurationToken(trWithStaticFallback('week ago', 'week ago', 'week ago')),
          labelPlural: stripDurationToken(trWithStaticFallback('weeks ago', 'weeks ago', 'weeks ago')),
        },
        {
          value: 'month',
          labelSingular: stripDurationToken(trWithStaticFallback('month ago', 'month ago', 'month ago')),
          labelPlural: stripDurationToken(trWithStaticFallback('months ago', 'months ago', 'months ago')),
        },
        {
          value: 'year',
          labelSingular: stripDurationToken(trWithStaticFallback('year ago', 'year ago', 'year ago')),
          labelPlural: stripDurationToken(trWithStaticFallback('years ago', 'years ago', 'years ago')),
        },
      ];
    },
    [trWithStaticFallback]
  );
  const formatRelativeOnsetDate = useCallback(
    (date: Date, unit: V2QsRelativeOnsetUnit) => {
      const localeCode = forcedLocaleCode || 'en';

      switch (unit) {
        case 'minute':
        case 'hour':
          return new Intl.DateTimeFormat(localeCode, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            weekday: 'long',
            hour: '2-digit',
            hour12: true,
          }).format(date);
        case 'day':
        case 'week':
          return new Intl.DateTimeFormat(localeCode, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            weekday: 'long',
          }).format(date);
        case 'month':
          return new Intl.DateTimeFormat(localeCode, {
            month: 'long',
            year: 'numeric',
          }).format(date);
        case 'year':
          return new Intl.DateTimeFormat(localeCode, {
            year: 'numeric',
          }).format(date);
        default:
          return new Intl.DateTimeFormat(localeCode, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            weekday: 'long',
          }).format(date);
      }
    },
    [forcedLocaleCode]
  );
  const notePlaceholder = trWithStaticFallback(
    'label-describe-in-detail',
    'Describe in detail',
    'label-describe-in-detail'
  );
  const noteRequiredMessage = trWithStaticFallback(
    'label-detailed-description-required',
    'Detailed description required',
    'label-detailed-description-required'
  );

  // 기본 props
  const commonProps = {
    disabled,
    error: false,
    fullWidth: true,
  };
  const noOptionsConfigured = (
    <div className="p-2 text-sm text-gray-500 bg-gray-100 rounded">
      No options configured
    </div>
  );

  // inputType별 컴포넌트 렌더링
  switch (inputType) {
    // Text 입력 계열
    case 'text':
      return (
        <V2QsText
          {...commonProps}
          value={(value as string) || ''}
          onChange={handleTextChange}
          placeholder={trWithStaticFallback('placeholder-enter-text', 'Enter text', 'placeholder-enter-text')}
        />
      );

    case 'textarea':
      return (
        <V2QsTextarea
          {...commonProps}
          value={(value as string) || ''}
          onChange={handleTextChange}
          placeholder={trWithStaticFallback('placeholder-enter-text', 'Enter text', 'placeholder-enter-text')}
          minLength={typeOptions?.min}
          maxLength={typeOptions?.max}
        />
      );

    case 'number':
      return (
        <V2QsNumber
          {...commonProps}
          value={(value as number) ?? undefined}
          onChange={handleNumberChange}
          min={typeOptions?.min}
          max={typeOptions?.max}
        />
      );

    case 'number_unit_select': {
      const unitOptions = answerKeys?.map((key) => {
        const optionData = answerOptionsMap?.[key] as Record<string, unknown> | undefined;
        return {
          key,
          label: (optionData?.label as string) || key,
        };
      }) || [];

      if (unitOptions.length === 0) {
        return (
          <div className="text-error font-medium">
            {trWithStaticFallback('label-missing-answer', 'Missing an answer', 'label-missing-answer')}
          </div>
        );
      }

      const numberUnitValue = value as { value?: number; unit?: string } | undefined;

      return (
        <V2QsNumber
          {...commonProps}
          value={numberUnitValue?.value}
          onChange={(newValue) => {
            onChange({
              value: newValue,
              unit: numberUnitValue?.unit || unitOptions[0]?.key,
            } as unknown as LiveTestingAnswerValue);
          }}
          units={unitOptions}
          selectedUnit={numberUnitValue?.unit || unitOptions[0]?.key}
          onUnitChange={(newUnit) => {
            onChange({
              value: numberUnitValue?.value,
              unit: newUnit,
            } as unknown as LiveTestingAnswerValue);
          }}
          min={typeOptions?.min}
          max={typeOptions?.max}
        />
      );
    }

    // 선택 계열
    case 'select':
    case 'radio':
      if (selectOptions.length === 0) {
        return noOptionsConfigured;
      }
      return (
        <V2QsSelect
          {...commonProps}
          mode="radio"
          options={selectOptions}
          value={(value as V2QsSelectValue) || { selectedKeys: [], notesForKeys: {} }}
          onChange={handleSelectChange}
          notePlaceholder={notePlaceholder}
        />
      );

    case 'multiChoice':
      if (selectOptions.length === 0) {
        return noOptionsConfigured;
      }
      return (
        <V2QsSelect
          {...commonProps}
          mode="checkbox"
          options={selectOptions}
          value={(value as V2QsSelectValue) || { selectedKeys: [], notesForKeys: {} }}
          onChange={handleSelectChange}
          notePlaceholder={notePlaceholder}
        />
      );

    // 날짜/시간 계열
    case 'date':
      return (
        <V2QsDate
          {...commonProps}
          value={(value as string) || ''}
          onChange={handleTextChange}
          placeholder={trWithStaticFallback('label-select', 'Select...', 'label-select')}
        />
      );

    case 'date_dob':
      return (
        <V2QsDateDob
          {...commonProps}
          value={(value as string) || ''}
          onChange={handleTextChange}
          placeholder={trWithStaticFallback('mon-day-year', 'Month / Day / Year', 'mon-day-year')}
          hint={trWithStaticFallback('mon-day-year', 'Month / Day / Year', 'mon-day-year')}
          ageRestrictionCloseText={trWithStaticFallback('btn-close', 'Close', 'btn-close')}
        />
      );

    case 'datetime':
      return (
        <V2QsDatetime
          {...commonProps}
          value={(value as string) || ''}
          onChange={handleTextChange}
          placeholder={trWithStaticFallback('label-select', 'Select...', 'label-select')}
        />
      );

    case 'time':
      return (
        <V2QsTime
          {...commonProps}
          value={(value as string) || ''}
          onChange={handleTextChange}
          placeholder={trWithStaticFallback('label-select', 'Select...', 'label-select')}
        />
      );

    // sx_select (증상 선택)
    case 'sx_select':
      return (
        <LiveTestingSxSelectField
          question={question}
          value={(value as V2QsSelectValue) || { selectedKeys: [], notesForKeys: {} }}
          onChange={(newValue) => onChange(newValue as LiveTestingAnswerValue)}
          disabled={disabled}
        />
      );

    // 특수 입력 계열
    case 'range': {
      const rangeMin = typeOptions?.min ?? 0;
      const rangeMax = typeOptions?.max ?? 10;
      const scaleLabelsValues: number[] = (question.typeOptions?.scaleLabels || [])
        .filter((v: number) => v >= rangeMin && v <= rangeMax); // filter out-of-range values
      const resolvedLabels = scaleLabelsValues
        .map((val) => {
          const key = genScaleLabelKey(question.questionKey, val);
          const translated = t(key, null, { fallbackValue: null });
          const text = translated && translated !== key ? translated : '';
          return text ? { value: val, text } : null;
        })
        .filter(Boolean) as Array<{ value: number; text: string }>;
      return (
        <V2QsRange
          {...commonProps}
          value={(value as number) ?? rangeMin}
          onChange={(v: number) => onChange(v)}
          min={rangeMin}
          max={rangeMax}
          step={typeOptions?.step ?? 1}
          labels={resolvedLabels}
        />
      );
    }

    // PROM 타입: EQ-VAS (0~100 세로 눈금자)
    case 'eq_vas': {
      const eqVasLabel = normalizeQuestionLabelForSurveyLocale({
        locale,
        label: question.label,
      });
      const qKey = question.questionKey;
      const resolveEqVasLabel = (key: string, fallback: string) => {
        const translated = t(key, null, { fallbackValue: null });
        if (translated && translated !== key) return translated;
        return fallback;
      };
      return (
        <V2QsEqVas
          value={(value as number) ?? undefined}
          onChange={(v: number) => onChange(v)}
          topLabel={resolveEqVasLabel(genEqVasTopLabelKey(qKey), '')}
          bottomLabel={resolveEqVasLabel(genEqVasBottomLabelKey(qKey), '')}
          valueBoxLabel={resolveEqVasLabel(genEqVasValueBoxLabelKey(qKey), '')}
          title={
            <span>
              {eqVasLabel}
              {!question.optional && ' *'}
            </span>
          }
          leftText={(() => {
            const raw = question.descriptionInline || (typeOptions as Record<string, unknown> | undefined)?.leftText as string | undefined;
            if (!raw) return undefined;
            if (/^[a-z0-9][-a-z0-9]*$/.test(raw)) return undefined;
            return raw;
          })()}
          disabled={disabled}
        />
      );
    }

    case 'blood_pressure':
      return (
        <V2QsBloodPressure
          {...commonProps}
          value={(value as V2QsBloodPressureValue) || {}}
          onChange={handleBloodPressureChange}
        />
      );

    case 'frequency':
      return (
        <V2QsFrequency
          {...commonProps}
          value={(value as V2QsFrequencyValue) || {}}
          onChange={handleFrequencyChange}
        />
      );

    case 'relative_onset':
    case 'datetime_relative_onset':
      return (
        <V2QsRelativeOnset
          {...commonProps}
          value={(value as V2QsRelativeOnsetValue) || {}}
          onChange={handleRelativeOnsetChange}
          unitOptions={localizedUnitOptions as any}
          formatCalculatedDate={formatRelativeOnsetDate}
        />
      );

    // select_sheet (single/multi)
    case 'select_sheet': {
      if (selectOptions.length === 0) {
        return noOptionsConfigured;
      }
      const isMulti = (typeOptions as { multi?: boolean })?.multi === true;
      const forceDropdown = (typeOptions as { forceDropdown?: boolean })?.forceDropdown === true;
      const useLengthyDropdown = forceDropdown || selectOptions.length > 4;

      if (useLengthyDropdown) {
        return (
          <V2QsSelectSheet
            options={selectOptions}
            value={(value as V2QsSelectSheetValue) || { selectedKeys: [], notesForKeys: {} }}
            onChange={(newValue) => onChange(newValue as LiveTestingAnswerValue)}
            single={!isMulti}
            placeholder={trWithStaticFallback('label-select', 'Select', 'label-select')}
            closeButtonText={trWithStaticFallback('btn-close', 'Close', 'btn-close')}
            notePlaceholder={notePlaceholder}
            noteRequiredMessage={noteRequiredMessage}
            disabled={disabled}
          />
        );
      }

      return (
        <V2QsSelect
          {...commonProps}
          mode={isMulti ? 'checkbox' : 'radio'}
          options={selectOptions}
          value={(value as V2QsSelectValue) || { selectedKeys: [], notesForKeys: {} }}
          onChange={handleSelectChange}
          notePlaceholder={notePlaceholder}
        />
      );
    }

    case 'select_sheet_single': {
      if (selectOptions.length === 0) {
        return noOptionsConfigured;
      }
      const forceDropdown = (typeOptions as { forceDropdown?: boolean })?.forceDropdown === true;
      const useLengthyDropdown = forceDropdown || selectOptions.length > 4;

      if (useLengthyDropdown) {
        return (
          <V2QsSelectSheet
            options={selectOptions}
            value={(value as V2QsSelectSheetValue) || { selectedKeys: [], notesForKeys: {} }}
            onChange={(newValue) => onChange(newValue as LiveTestingAnswerValue)}
            single
            placeholder={trWithStaticFallback('label-select', 'Select', 'label-select')}
            closeButtonText={trWithStaticFallback('btn-close', 'Close', 'btn-close')}
            notePlaceholder={notePlaceholder}
            noteRequiredMessage={noteRequiredMessage}
            disabled={disabled}
          />
        );
      }

      return (
        <V2QsSelect
          {...commonProps}
          mode="radio"
          options={selectOptions}
          value={(value as V2QsSelectValue) || { selectedKeys: [], notesForKeys: {} }}
          onChange={handleSelectChange}
          notePlaceholder={notePlaceholder}
        />
      );
    }

    case 'select_sheet_multi': {
      if (selectOptions.length === 0) {
        return noOptionsConfigured;
      }
      const forceDropdown = (typeOptions as { forceDropdown?: boolean })?.forceDropdown === true;
      const useLengthyDropdown = forceDropdown || selectOptions.length > 4;

      if (useLengthyDropdown) {
        return (
          <V2QsSelectSheet
            options={selectOptions}
            value={(value as V2QsSelectSheetValue) || { selectedKeys: [], notesForKeys: {} }}
            onChange={(newValue) => onChange(newValue as LiveTestingAnswerValue)}
            placeholder={trWithStaticFallback('label-select', 'Select', 'label-select')}
            closeButtonText={trWithStaticFallback('btn-close', 'Close', 'btn-close')}
            notePlaceholder={notePlaceholder}
            noteRequiredMessage={noteRequiredMessage}
            disabled={disabled}
          />
        );
      }

      return (
        <V2QsSelect
          {...commonProps}
          mode="checkbox"
          options={selectOptions}
          value={(value as V2QsSelectValue) || { selectedKeys: [], notesForKeys: {} }}
          onChange={handleSelectChange}
          notePlaceholder={notePlaceholder}
        />
      );
    }

    case 'body_select':
    case 'body_select_whole':
      return (
        <LiveTestingBodySelectTreeField
          question={question}
          value={(value as BodySelectTreeValue) || { selectedKeyGroups: [] }}
          onChange={(v) => onChange(v as LiveTestingAnswerValue)}
          disabled={disabled}
        />
      );

    case 'body_select_head':
      return (
        <LiveTestingHeadSelectField
          question={question}
          value={(value as BodySelectValue) || { answerKeys: [] }}
          onChange={(v) => onChange(v as LiveTestingAnswerValue)}
          disabled={disabled}
          locale={locale}
        />
      );

    case 'body_select_belly':
    case 'body_select_chest':
    case 'body_select_back':
    case 'body_select_face':
    case 'body_select_limbs':
    case 'body_select_mouth':
    case 'body_select_dental':
    case 'body_select_sensory':
    case 'body_select_subpart':
    case 'body_select_arm':
    case 'body_select_leg':
    case 'body_select_joints':
      return (
        <LiveTestingBodySelectField
          question={question}
          value={(value as BodySelectValue) || { answerKeys: [] }}
          onChange={(v) => onChange(v as LiveTestingAnswerValue)}
          disabled={disabled}
          isMale={isMale}
        />
      );

    // 지원하지 않는 타입 (모달 내에서는 제한적인 타입만 지원)
    default:
      return (
        <div className="p-2 text-sm text-gray-500 bg-gray-100 rounded">
          Unsupported input type in modal: {inputType}
        </div>
      );
  }
}

export default memo(LiveTestingQuestionFieldInner);
